// Reports landing page (AWS cost plan Phase 3, Task 13, 2026-11-02).
//
// One sidebar link opens this page: every report the user can open, as a card,
// grouped by topic, with a search box, a Starred row and a Recently viewed row.
// Reports moving over from QuickSight land here as new cards in the catalog's
// `reports` department, never as new sidebar links.
//
// Stars are the existing per-user pins (server-side, so they survive signing
// out, and a starred report also shows in the sidebar's Pinned section).
// Recently viewed is per-browser (lib/reportRecents.ts).

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import type { Coverage, Tool } from '@/data/tools.config';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { FilterLabel } from '@/components/FilterLabel';
import { useSession } from '@/lib/useSession';
import { useAddPin, usePins, useRemovePin } from '@/lib/usePins';
import { toolHrefFor } from '@/lib/toolRouting';
import { readRecentReports } from '@/lib/reportRecents';
import {
  REPORTS_DEPT_ID,
  groupReportsByTopic,
  matchesReportSearch,
  pickInOrder,
  visibleReports,
} from '@/lib/reportsPage';

const RECENT_SHOWN = 4;

const searchCls =
  'w-full border border-amp-line rounded px-2.5 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted';

export function Reports() {
  const { user } = useSession();
  const perms = user?.permissions;
  const superAdmin = !!user?.superAdmin;
  const reports = useMemo(() => visibleReports(perms, superAdmin), [perms, superAdmin]);
  const { pins } = usePins();
  const [query, setQuery] = useState('');
  // Read once per visit; the list only changes when the user opens a report,
  // which navigates away from this page.
  const [recentIds] = useState(readRecentReports);

  const starred = useMemo(
    () =>
      pickInOrder(
        [...pins].sort((a, b) => a.sort_order - b.sort_order).map((p) => p.tool_slug),
        reports,
      ),
    [pins, reports],
  );
  const recent = useMemo(() => pickInOrder(recentIds, reports).slice(0, RECENT_SHOWN), [recentIds, reports]);
  const searching = query.trim().length > 0;
  const results = useMemo(() => reports.filter((t) => matchesReportSearch(t, query)), [reports, query]);
  const groups = useMemo(() => groupReportsByTopic(reports), [reports]);

  return (
    <div className="pb-20">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Cockpit</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Reports</h1>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Every report you have access to. Star a report to keep it at the top.
        </p>
      </div>

      {reports.length === 0 ? (
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
          You do not have access to any reports yet. Ask IT if you need one.
        </p>
      ) : (
        <>
          <div className="mb-6 max-w-md">
            <FilterLabel htmlFor="reports-search">Search</FilterLabel>
            <ClearableSearchInput
              id="reports-search"
              value={query}
              onChange={setQuery}
              placeholder="Find a report by name"
              className={searchCls}
            />
          </div>

          {searching ? (
            <Section title={`${results.length} ${results.length === 1 ? 'report' : 'reports'} found`}>
              {results.length === 0 ? (
                <p className="text-sm text-amp-ink dark:text-amp-dark-ink">No report matches that search.</p>
              ) : (
                <CardGrid tools={results} />
              )}
            </Section>
          ) : (
            <>
              {starred.length > 0 && (
                <Section title="Starred">
                  <CardGrid tools={starred} />
                </Section>
              )}
              {recent.length > 0 && (
                <Section title="Recently viewed">
                  <CardGrid tools={recent} />
                </Section>
              )}
              {groups.map((g) => (
                <Section key={g.topic} title={g.topic}>
                  <CardGrid tools={g.tools} />
                </Section>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-amp-ink dark:text-amp-dark-ink">
        {title}
      </h2>
      {children}
    </section>
  );
}

function CardGrid({ tools }: { tools: Tool[] }) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
      {tools.map((t) => (
        <ReportCard key={t.id} tool={t} />
      ))}
    </div>
  );
}

function ReportCard({ tool }: { tool: Tool }) {
  return (
    <Link
      to={toolHrefFor(REPORTS_DEPT_ID, tool.id)}
      aria-label={`Open ${tool.name}`}
      className="block h-full rounded-lg no-underline focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40"
    >
      <article className="flex h-full flex-col gap-2 rounded-lg bg-amp-white px-4 pb-3.5 pt-3.5 shadow-1 transition-[box-shadow,transform] duration-normal ease-out hover:-translate-y-px hover:shadow-2 dark:bg-amp-dark-surface">
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-amp-ink dark:text-amp-dark-ink" style={{ fontSize: 15 }}>
            {tool.name}
          </div>
          <StarToggle tool={tool} />
        </div>
        <p
          className="m-0 text-amp-ink dark:text-amp-dark-ink"
          style={{
            fontSize: 13,
            fontWeight: 400,
            lineHeight: 1.5,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {tool.desc}
        </p>
        <CoversLabels covers={tool.covers} />
      </article>
    </Link>
  );
}

// Which parts of the business a report covers (catalog `covers`), as small
// labels at the foot of the card, in a fixed order. No `covers`, no labels.
const COVERAGE_LABELS: Record<Coverage, string> = {
  'lp-stores': 'L&P Stores',
  'lp-online': 'L&P Online',
  'hb-store': "Hammond's Store",
  'hb-online': "Hammond's Online",
  'hb-wholesale': "Hammond's Wholesale",
  warehouse: 'Warehouse',
};
const COVERAGE_ORDER = Object.keys(COVERAGE_LABELS) as Coverage[];

function CoversLabels({ covers }: { covers: Tool['covers'] }) {
  if (!covers || covers.length === 0) return null;
  return (
    <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
      {COVERAGE_ORDER.filter((c) => covers.includes(c)).map((c) => COVERAGE_LABELS[c]).map((label) => (
        <span
          key={label}
          className="rounded-full border border-amp-line px-2 py-0.5 text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted"
          style={{ fontSize: 11, lineHeight: 1.4 }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function StarToggle({ tool }: { tool: Tool }) {
  const { user } = useSession();
  // Stars are pins, which need core:write; hide the control for anyone without
  // it (e.g. store accounts) rather than show a button that fails.
  const canStar = !!user?.permissions?.includes('core:write');
  const { pinnedSet } = usePins();
  const addPin = useAddPin();
  const removePin = useRemovePin();
  const starred = pinnedSet.has(tool.id);
  const busy = addPin.isPending || removePin.isPending;
  const label = starred ? `Unstar ${tool.name}` : `Star ${tool.name}`;

  if (!canStar) return null;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;
        (starred ? removePin : addPin).mutate(tool.id);
      }}
      aria-label={label}
      aria-pressed={starred}
      title={label}
      className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-amp-ink-muted transition-colors duration-fast ease-out hover:bg-amp-cream-warm hover:text-amp-olive focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amp-olive/40 dark:text-amp-dark-ink-muted dark:hover:bg-amp-dark-surface-warm dark:hover:text-amp-olive-dark"
      style={{ opacity: busy ? 0.5 : 1, cursor: busy ? 'wait' : 'pointer' }}
    >
      <Star aria-hidden size={15} fill={starred ? 'currentColor' : 'none'} strokeWidth={starred ? 2 : 1.75} />
    </button>
  );
}
